import { supabase, getSupabaseUrl } from '../supabase/client';
import { hasTurtleImport, stripTurtleImport, injectTurtleModule, resetTurtleState, extractTurtleSvg } from './turtleModule';
import { translateErrorToChinese, getInputLineOffset } from '../utils/errorLineAdjust';

export interface PythonTestCase {
  id: string;
  input: string;
  expectedOutput: string;
  isHidden: boolean;
  scoreWeight: number;
}

export interface TestResult {
  testCaseId: string;
  passed: boolean;
  actualOutput: string;
  expectedOutput: string;
  executionTimeMs: number;
  error?: string;
}

export interface JudgeResult {
  success: boolean;
  results: TestResult[];
  totalScore: number;
  maxScore: number;
  passedCount: number;
  totalCount: number;
  executionTimeMs: number;
  memoryUsedMb: number;
  error?: string;
}

export interface PythonQuestion {
  id: string;
  paper_id: string;
  content: string;
  starter_code: string;
  reference_solution: string;
  time_limit_ms: number;
  memory_limit_mb: number;
  score: number;
  difficulty: string;
}

export interface PythonSubmission {
  id: string;
  user_id: string;
  question_id: string;
  paper_id: string;
  code: string;
  status: 'pending' | 'judging' | 'accepted' | 'wrong_answer' | 'time_limit_exceeded' | 'memory_limit_exceeded' | 'runtime_error';
  score: number;
  execution_time_ms: number;
  memory_used_mb: number;
  test_results: TestResult[];
  submitted_at: string;
}

// Pyodide 实例管理
let pyodideInstance: any = null;
let pyodideLoading: Promise<any> | null = null;
let pyodideLoadError: string | null = null;

// Pyodide 加载状态类型
export type PyodideStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * 获取 Pyodide 当前状态
 */
export function getPyodideStatus(): { state: PyodideStatus; error?: string } {
  if (pyodideInstance) {
    return { state: 'ready' };
  }
  if (pyodideLoading) {
    return { state: 'loading' };
  }
  if (pyodideLoadError) {
    return { state: 'error', error: pyodideLoadError };
  }
  return { state: 'idle' };
}

/**
 * 重置 Pyodide 状态（用于清理错误状态）
 */
export function resetPyodide() {
  pyodideInstance = null;
  pyodideLoading = null;
  pyodideLoadError = null;
}

/**
 * 初始化 Pyodide（带重试机制）
 * @param maxRetries 最大重试次数，默认 2 次
 */
export async function initPyodideWithRetry(maxRetries = 2): Promise<any> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await initPyodide();
    } catch (err: any) {
      pyodideLoadError = err.message || 'Pyodide 加载失败';

      if (attempt === maxRetries) {
        // 最后一次重试也失败，抛出错误
        throw err;
      }

      console.warn(`Pyodide 加载失败，第 ${attempt + 1}/${maxRetries} 次重试...`, err.message);
      // 等待 2 秒后重试
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }

  // 理论上不会到达这里
  throw new Error('Pyodide 加载失败');
}

export async function initPyodide() {
  if (pyodideInstance) return pyodideInstance;
  if (pyodideLoading) return pyodideLoading;

  pyodideLoading = new Promise(async (resolve, reject) => {
    try {
      if ((window as any).loadPyodide) {
        pyodideInstance = await (window as any).loadPyodide({
          indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/',
        });
        pyodideLoadError = null;
        resolve(pyodideInstance);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/pyodide.js';
      script.onload = async () => {
        try {
          const loadPyodide = (window as any).loadPyodide;
          if (!loadPyodide) {
            reject(new Error('loadPyodide not found on window'));
            return;
          }
          pyodideInstance = await loadPyodide({
            indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/',
          });
          pyodideLoadError = null;
          resolve(pyodideInstance);
        } catch (err) {
          reject(err);
        }
      };
      script.onerror = () => reject(new Error('Failed to load Pyodide script'));
      document.head.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });

  return pyodideLoading;
}

export async function runPythonLocal(
  code: string,
  input: string = ''
): Promise<{ output: string; error?: string; svg?: string | null }> {
  try {
    const pyodide = await initPyodide();

    if (hasTurtleImport(code)) {
      await injectTurtleModule(pyodide);
      await resetTurtleState(pyodide);
      const strippedCode = stripTurtleImport(code);

      let stdout = '';
      pyodide.setStdout({ batched: (text: string) => { stdout += text + '\n'; } });
      pyodide.setStderr({ batched: (text: string) => { stdout += text + '\n'; } });

      if (input) {
        pyodide.globals.set('input_data', input);
        const wrappedCode = `
import sys
from io import StringIO
sys.stdin = StringIO(input_data)
${strippedCode}
`;
        await pyodide.runPythonAsync(wrappedCode);
      } else {
        await pyodide.runPythonAsync(strippedCode);
      }

      const svgResult = await extractTurtleSvg(pyodide);

      return { output: stdout.trim(), svg: svgResult };
    }

    let stdout = '';
    pyodide.setStdout({ batched: (text: string) => { stdout += text + '\n'; } });
    pyodide.setStderr({ batched: (text: string) => { stdout += text + '\n'; } });

    if (input) {
      pyodide.globals.set('input_data', input);
      const wrappedCode = `
import sys
from io import StringIO
sys.stdin = StringIO(input_data)
${code}
`;
      await pyodide.runPythonAsync(wrappedCode);
    } else {
      await pyodide.runPythonAsync(code);
    }

    return { output: stdout.trim() };
  } catch (error: any) {
    const lineOffset = getInputLineOffset(Boolean(input));
    const translatedError = translateErrorToChinese(error.message || '执行失败', lineOffset);
    return { output: '', error: translatedError };
  }
}

export async function judgePythonCode(
  code: string,
  testCases: PythonTestCase[],
  timeLimitMs: number = 1000,
  memoryLimitMb: number = 64
): Promise<JudgeResult> {
  try {
    const session = (await supabase.auth.getSession()).data.session;
    const authHeaders = session ? { Authorization: `Bearer ${session.access_token}` } : {};

    const response = await fetch(`${getSupabaseUrl()}/functions/v1/code-grading`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeaders as Record<string, string>),
      },
      body: JSON.stringify({
        action: 'python_judge',
        code,
        testCases,
        timeLimitMs,
        memoryLimitMb,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || '判题请求失败');
    }

    return result as JudgeResult;
  } catch (error: any) {
    return {
      success: false,
      results: [],
      totalScore: 0,
      maxScore: 0,
      passedCount: 0,
      totalCount: testCases.length,
      executionTimeMs: 0,
      memoryUsedMb: 0,
      error: error.message || '判题服务异常',
    };
  }
}

export async function getPythonQuestion(questionId: string): Promise<PythonQuestion | null> {
  const { data, error } = await supabase
    .from('python_questions')
    .select('*')
    .eq('id', questionId)
    .maybeSingle();

  if (error) {
    
    return null;
  }

  return data as PythonQuestion;
}

export async function getPythonTestCases(questionId: string): Promise<PythonTestCase[]> {
  const { data, error } = await supabase
    .from('python_test_cases')
    .select('*')
    .eq('question_id', questionId)
    .order('sort_order', { ascending: true });

  if (error) {
    
    return [];
  }

  return (data || []).map((tc: any) => ({
    id: tc.id,
    input: tc.input_data || '',
    expectedOutput: tc.expected_output,
    isHidden: tc.is_hidden || false,
    scoreWeight: tc.score_weight || 1,
  }));
}

export async function savePythonSubmission(
  submission: Partial<PythonSubmission>
): Promise<PythonSubmission | null> {
  const { data, error } = await supabase
    .from('python_submissions')
    .insert(submission as any)
    .select()
    .maybeSingle();

  if (error) {
    
    return null;
  }

  return data as unknown as PythonSubmission;
}

export async function getPythonSubmissions(
  userId: string,
  questionId?: string
): Promise<PythonSubmission[]> {
  let query = supabase
    .from('python_submissions')
    .select('*')
    .eq('user_id', userId)
    .order('submitted_at', { ascending: false });

  if (questionId) {
    query = query.eq('question_id', questionId);
  }

  const { data, error } = await query;

  if (error) {
    
    return [];
  }

  return (data || []) as unknown as PythonSubmission[];
}

export function getStatusText(status: string): string {
  const statusMap: Record<string, string> = {
    pending: '等待判题',
    judging: '判题中',
    accepted: '通过',
    wrong_answer: '答案错误',
    time_limit_exceeded: '超时',
    memory_limit_exceeded: '内存超限',
    runtime_error: '运行时错误',
  };
  return statusMap[status] || status;
}

export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    pending: 'text-gray-500',
    judging: 'text-blue-500',
    accepted: 'text-green-500',
    wrong_answer: 'text-red-500',
    time_limit_exceeded: 'text-orange-500',
    memory_limit_exceeded: 'text-orange-500',
    runtime_error: 'text-red-500',
  };
  return colorMap[status] || 'text-gray-500';
}

// ==================== 双引擎智能分流 ====================

/**
 * 检测代码是否需要使用云端执行
 *
 * 判断规则：
 * 1. 包含 input() 调用 - 本地无法处理交互式输入
 * 2. 深层嵌套（超过 3 层缩进）- 可能是复杂算法
 * 3. 多个循环结构 - 可能执行时间长
 * 4. 代码行数过多（>200 行）- 复杂程序
 */
export function shouldUseCloud(code: string): boolean {
  // 检测 input() 调用
  if (/\binput\s*\(/.test(code)) {
    return true;
  }

  // 检测深层嵌套（简单启发式：最大缩进空格数）
  const lines = code.split('\n');
  const maxIndent = Math.max(
    ...lines.map(line => {
      const match = line.match(/^(\s*)/);
      return match ? match[1].length : 0;
    })
  );
  if (maxIndent > 12) { // 假设每层 4 空格，12 空格 = 3 层
    return true;
  }

  // 检测大型循环
  const forCount = (code.match(/\bfor\b/g) || []).length;
  const whileCount = (code.match(/\bwhile\b/g) || []).length;
  if (forCount > 3 || whileCount > 2) {
    return true;
  }

  // 代码行数过多
  if (lines.length > 200) {
    return true;
  }

  return false;
}

/**
 * 云端执行封装
 *
 * 将单次预览请求包装成判题格式，调用 code-grading 云函数
 */
async function runPythonCloud(
  code: string,
  input: string = ''
): Promise<{ output: string; error?: string; svg?: string | null; engine: 'cloud' }> {
  try {
    // 构造单个测试用例用于预览
    const testCase: PythonTestCase = {
      id: 'preview',
      input,
      expectedOutput: '', // 预览模式不校验输出
      isHidden: false,
      scoreWeight: 1,
    };

    const result = await judgePythonCode(code, [testCase], 15000, 128);

    if (!result.success || result.results.length === 0) {
      throw new Error(result.error || '云端执行失败');
    }

    const testResult = result.results[0];
    return {
      output: testResult.actualOutput,
      error: testResult.error,
      svg: null, // 云端暂不支持 Turtle 图形
      engine: 'cloud' as const,
    };
  } catch (err: any) {
    throw new Error(`云端执行失败: ${err.message}`);
  }
}

/**
 * 带重试的本地执行
 *
 * 如果首次执行失败，尝试重置 Pyodide 后重试一次
 */
async function runPythonWithRetry(
  code: string,
  input: string = '',
  maxRetries = 1
): Promise<{ output: string; error?: string; svg?: string | null; engine: 'local' }> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await runPythonLocal(code, input);
      return {
        ...result,
        engine: 'local' as const,
      };
    } catch (err: any) {
      if (attempt === maxRetries) {
        throw err;
      }

      console.warn(`本地执行失败，第 ${attempt + 1} 次重试...`, err.message);
      // 重置 Pyodide 状态后重试
      resetPyodide();
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  }

  // 理论上不会到达这里
  throw new Error('本地执行失败');
}

/**
 * 统一执行入口 - 智能选择本地或云端执行
 *
 * @param code Python 代码
 * @param input 输入数据
 * @param options 可选配置
 * @param options.forceCloud 强制使用云端执行
 * @returns 执行结果，包含使用的引擎标识
 */
export async function runPythonSmart(
  code: string,
  input: string = '',
  options?: { forceCloud?: boolean }
): Promise<{ output: string; error?: string; svg?: string | null; engine: 'local' | 'cloud' }> {
  // 1. 检测是否需要云端执行
  const useCloud = options?.forceCloud || shouldUseCloud(code);

  if (useCloud) {
    try {
      return await runPythonCloud(code, input);
    } catch (err: any) {
      console.error('[Python] 云端执行失败:', err.message);
      throw err;
    }
  }

  // 2. 本地执行：带重试和降级
  try {
    return await runPythonWithRetry(code, input);
  } catch (err: any) {
    console.warn('[Python] 本地执行失败，降级到云端:', err.message);

    // 降级到云端
    try {
      return await runPythonCloud(code, input);
    } catch (cloudErr: any) {
      // 云端也失败，抛出原始错误
      throw err;
    }
  }
}
